import { logger } from "../../../logger"
/**
 * Shadow Debug Test
 *
 * This script helps test the shadow rendering cache functionality
 * by enabling debug logging and monitoring cache behavior.
 */

import { ShadowRendering } from "./shadow_rendering"
import { ShadowPerformanceMonitor } from "./shadow_performance_monitor"

export class ShadowDebugTest {
    private shadowRendering: ShadowRendering
    private performanceMonitor: ShadowPerformanceMonitor
    private testFrameCount: number = 0
    private maxTestFrames: number = 100

    constructor(shadowRendering: ShadowRendering) {
        this.shadowRendering = shadowRendering
        this.performanceMonitor = new ShadowPerformanceMonitor(shadowRendering)

        // Enable debug logging
        this.shadowRendering.debugHashCalculation = true
    }

    /**
     * Run a test to verify cache behavior
     */
    runTest = () => {
        logger.info({ tags: ["shadow", "debug"] }, "[Shadow Debug Test] Starting cache test...")
        this.testFrameCount = 0
        this.performanceMonitor.reset()

        // Run test for a few frames
        const testInterval = setInterval(() => {
            this.performanceMonitor.update()
            this.testFrameCount++

            if (this.testFrameCount >= this.maxTestFrames) {
                clearInterval(testInterval)
                this.logTestResults()
            }
        }, 16) // ~60 FPS
    }

    private logTestResults = () => {
        const metrics = this.performanceMonitor.getMetrics()
        logger.info({ tags: ["shadow", "debug"] }, "[Shadow Debug Test] Test Results:")
        logger.info({ tags: ["shadow", "debug"] }, `  Total Frames: ${metrics.totalFrames}`)
        logger.info({ tags: ["shadow", "debug"] }, `  Cache Hit Rate: ${metrics.cacheHitRate.toFixed(1)}%`)
        logger.info({ tags: ["shadow", "debug"] }, `  Texture Regen Rate: ${metrics.textureRegenRate.toFixed(1)}%`)
        logger.info({ tags: ["shadow", "debug"] }, `  Cache Size: ${metrics.cacheSize}/${metrics.maxCacheSize}`)
        logger.info({ tags: ["shadow", "debug"] }, `  Needs Update: ${metrics.needsUpdate}`)

        // Check if cache is working
        if (metrics.cacheHitRate > 0) {
            logger.info({ tags: ["shadow", "debug"] }, "✅ Cache is working!")
        } else {
            logger.info({ tags: ["shadow", "debug"] }, "❌ Cache is not working - 0% hit rate")
        }
    }

    /**
     * Force a cache clear to test cache miss behavior
     */
    forceCacheClear = () => {
        logger.info({ tags: ["shadow", "debug"] }, "[Shadow Debug Test] Forcing cache clear...")
        this.shadowRendering.forceTextureUpdate()
    }

    /**
     * Get current cache statistics
     */
    getCacheStats = () => {
        return this.shadowRendering.getCacheStats()
    }

    /**
     * Enable/disable debug logging
     */
    setDebugLogging = (enabled: boolean) => {
        this.shadowRendering.debugHashCalculation = enabled
        logger.info({ tags: ["shadow", "debug"] }, `[Shadow Debug Test] Debug logging ${enabled ? "enabled" : "disabled"}`)
    }
}

/**
 * Usage example:
 *
 * // In your main client code:
 * const shadowDebugTest = new ShadowDebugTest(shadowRendering)
 *
 * // Run the test
 * shadowDebugTest.runTest()
 *
 * // Or manually check cache stats
 * logger.info({ tags: ["shadow", "debug"] }, shadowDebugTest.getCacheStats())
 */
